import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Download, FileText, Loader2, ShieldCheck, ArrowRight, PlusCircle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";

export default function Contract() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [status, setStatus] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = `${API}/documents/contratto-segnalatore.pdf`;
    a.download = "contratto-segnalatore-pistone.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setDownloaded(true);
  };

  const load = async () => {
    const [c, s] = await Promise.all([
      api.get("/contract/current"),
      api.get("/me/contract-status"),
    ]);
    setContract(c.data);
    setStatus(s.data);
  };

  useEffect(() => { load().catch(() => toast.error("Impossibile caricare il contratto.")); }, []);

  const accept = async () => {
    setSubmitting(true);
    try {
      await api.post("/me/accept-contract");
      triggerDownload();
      await refreshUser();
      await load();
      setChecked(false);
      toast.success("Contratto firmato. Download avviato.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la firma.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!contract || !status) {
    return (
      <DashboardLayout title="Contratto segnalatore">
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>
      </DashboardLayout>
    );
  }

  const accepted = status.accepted;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");

  return (
    <DashboardLayout title="Contratto segnalatore">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="contract-page">
        {/* onboarding steps + explanation (before signing) */}
        {!accepted && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contract-onboarding">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#8a6d0f]">Come funziona — pochi passaggi</p>
            <ol className="mt-3 grid gap-2 sm:grid-cols-2">
              {[
                ["1", "Registrazione", "Fatto ✓", true],
                ["2", "Leggi come funziona", "Ci sei quasi", false],
                ["3", "Firma e scarica il contratto", "Passaggio corrente", false],
                ["4", "Inizia a segnalare", "Sblocchi le segnalazioni", false],
              ].map(([n, t, s, done]) => (
                <li key={n} className="flex items-start gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${done ? "bg-emerald-500 text-white" : "bg-[#0A1128] text-[#D4AF37]"}`}>{done ? "✓" : n}</span>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t}</p>
                    <p className="text-xs text-slate-400">{s}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-4 rounded-lg bg-[#0A1128] p-4 text-sm text-white/80 leading-relaxed">
              <strong className="text-[#D4AF37]">Tu segnali. Noi pensiamo al resto.</strong> Il tuo compito è solo <strong className="text-white">segnalare</strong> un
              proprietario potenzialmente interessato a vendere. Non fai mediazione, non gestisci visite né trattative: a tutto il resto pensiamo noi.
              Quando la segnalazione porta a un incarico, ti riconosciamo un <strong className="text-white">merito di fidelizzazione dall'8% al 12%</strong>,
              concordato e comunicato in piattaforma. Prima di iniziare, leggi e firma il contratto qui sotto: la firma viene registrata con data, ora e versione,
              e riceverai automaticamente una copia in PDF.
            </div>
          </div>
        )}

        {/* status banner */}
        <div className={`rounded-2xl border p-5 flex items-start gap-4 ${accepted ? "border-emerald-200 bg-emerald-50" : "border-[#D4AF37]/40 bg-[#D4AF37]/5"}`}
          data-testid="contract-status-banner">
          {accepted ? <ShieldCheck className="h-6 w-6 text-emerald-600 shrink-0" /> : <FileText className="h-6 w-6 text-[#8a6d0f] shrink-0" />}
          <div className="flex-1">
            {accepted ? (
              <>
                <p className="font-semibold text-emerald-800" data-testid="contract-signed-label">Contratto firmato — versione {status.accepted_version}</p>
                <p className="text-sm text-emerald-700 mt-0.5">Firmato il {fmt(status.accepted_at)}. Puoi inviare segnalazioni.</p>
              </>
            ) : (
              <>
                <p className="font-semibold text-[#8a6d0f]">Firma richiesta</p>
                <p className="text-sm text-slate-600 mt-0.5">Per poter inviare segnalazioni devi leggere e accettare i Termini e Condizioni del Programma Segnalatori (versione {contract.effective_date}).</p>
              </>
            )}
          </div>
          <a href={`${API}/documents/contratto-segnalatore.pdf`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#8a6d0f] hover:underline shrink-0" data-testid="contract-download-pdf">
            <Download className="h-4 w-4" /> PDF
          </a>
        </div>

        {/* contract body */}
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="px-6 pt-6 pb-4 border-b border-slate-100">
            <p className="font-display text-2xl font-semibold text-slate-900">{contract.title}</p>
            <p className="text-sm text-slate-500 mt-1">{contract.subtitle}</p>
            <div className="mt-3 text-xs text-slate-500 leading-relaxed">
              {contract.promoter.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </div>
          <div className="max-h-[52vh] overflow-y-auto px-6 py-5 space-y-6" data-testid="contract-text">
            {contract.sections.map((sec, i) => (
              <div key={i}>
                <h3 className="font-display text-base font-semibold text-slate-900 mb-2">{sec.heading}</h3>
                <div className="space-y-2">
                  {sec.paragraphs.map((p, j) => (
                    <p key={j} className="text-sm text-slate-600 leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900 mb-2">Accettazione del Programma</h3>
              <p className="text-sm text-slate-600 mb-2">Aderendo al Programma, il Segnalatore dichiara:</p>
              <ul className="space-y-1.5">
                {contract.acceptance.map((a, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-600 leading-relaxed">
                    <span className="text-[#8a6d0f]">•</span><span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs font-semibold text-slate-500 pt-2 border-t border-slate-100">
              Versione dei Termini e Condizioni: {contract.effective_date} ({contract.version})
            </p>
          </div>

          {/* accept box */}
          {!accepted && (
            <div className="px-6 py-5 border-t border-slate-100 bg-slate-50 rounded-b-2xl space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                  data-testid="contract-accept-checkbox" className="mt-0.5 h-5 w-5 accent-[#D4AF37]" />
                <span className="text-sm text-slate-700">
                  Dichiaro di aver letto, compreso e di accettare integralmente i Termini e Condizioni del Programma Segnalatori
                  “Pistone Parla Chiaro” (versione {contract.effective_date}).
                </span>
              </label>
              <button onClick={accept} disabled={!checked || submitting} data-testid="contract-accept-btn"
                className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Firma, scarica e accetta il contratto
              </button>
              <p className="text-xs text-slate-400">Firmando, scarichi automaticamente la tua copia del contratto in PDF.</p>
            </div>
          )}
        </div>

        {accepted && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="rounded-2xl border border-emerald-200 bg-white p-6 space-y-4" data-testid="contract-done-cta">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
              <div>
                <p className="font-display text-lg font-semibold text-slate-900">Tutto pronto!</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  La tua accettazione è registrata con data, ora e versione del contratto. Ora puoi inviare la tua prima segnalazione.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => navigate("/segnala")} data-testid="contract-go-segnala-btn"
                className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center justify-center gap-2">
                <PlusCircle className="h-4 w-4" /> Segnala un immobile <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={triggerDownload} data-testid="contract-redownload-btn"
                className="rounded-full border border-slate-300 px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-2">
                <Download className="h-4 w-4" /> Scarica di nuovo il contratto
              </button>
            </div>
            {downloaded && <p className="text-xs text-emerald-700">✔ Download del contratto avviato.</p>}
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
